/** Colour helpers for ColorField and Swatch (RGB integer triples 0..255, RPE `[r, g, b]`). */

import type { RGB } from '../../core/util/types';

function hex2(n: number): string {
  return clampChannel(n).toString(16).toUpperCase().padStart(2, '0');
}

/** Rounds and clamps a channel to an integer 0..255 (NaN -> 0). */
export function clampChannel(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(255, Math.max(0, Math.round(n)));
}

/** "#RRGGBB" (upper case). */
export function rgbToHex(c: RGB): string {
  return `#${hex2(c[0])}${hex2(c[1])}${hex2(c[2])}`;
}

/**
 * Parses "#RRGGBB", "RRGGBB", "#RGB" or "RGB" (any case, surrounding spaces ignored). Null when malformed.
 */
export function parseHex(src: string): RGB | null {
  const s = src.trim().replace(/^#/, '');
  if (/^[0-9a-f]{6}$/i.test(s)) {
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  }
  if (/^[0-9a-f]{3}$/i.test(s)) {
    const [r = '0', g = '0', b = '0'] = s.split('');
    return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16)];
  }
  return null;
}

/** Parses one channel typed as a whole number 0..255. Null when it is not one. */
export function parseChannel(src: string): number | null {
  const s = src.trim();
  if (!/^\d{1,3}$/.test(s)) return null;
  const n = Number(s);
  return n <= 255 ? n : null;
}

export function sameRgb(a: RGB, b: RGB): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
